export const PRACTICE_AREAS = {
  "diritto-del-lavoro": {
    slug: "diritto-del-lavoro",
    navName: "Diritto del lavoro",
    metaTitle: "Avvocato del lavoro a Palermo | Avv. Manfredi Bruno",
    metaDescription:
      "Assistenza legale in diritto del lavoro a Palermo: licenziamenti, stipendi non pagati, TFR, differenze retributive, demansionamento e controversie con il datore di lavoro.",
    h1: "Diritto del lavoro a Palermo",
    kicker: "Area di attività",
    intro:
      "Il lavoro è una parte centrale della vita di ogni persona. Quando qualcosa non funziona — un licenziamento, una retribuzione non pagata, un demansionamento — è importante capire con chiarezza quali diritti possono essere fatti valere e con quali strumenti. Assisto lavoratrici e lavoratori di Palermo e provincia nelle controversie con il datore di lavoro, nella fase stragiudiziale e, quando necessario, in giudizio.",
    sections: [
      {
        heading: "Quando è utile rivolgersi a un avvocato del lavoro",
        paragraphs: [
          "Molte controversie di lavoro possono essere risolte in via stragiudiziale, se affrontate tempestivamente e con la strategia corretta. Altre richiedono invece l'intervento del giudice. In entrambi i casi, il primo passo è una valutazione attenta dei documenti e dei fatti: solo così è possibile comprendere la reale solidità della propria posizione.",
        ],
        list: [
          "Licenziamento o intimazione di licenziamento",
          "Stipendi, mensilità o TFR non pagati",
          "Differenze retributive e straordinari non riconosciuti",
          "Demansionamento e modifiche peggiorative delle mansioni",
          "Mobbing e situazioni di disagio nell'ambiente di lavoro",
          "Contestazioni disciplinari e sanzioni conservative",
          "Dimissioni per giusta causa",
        ],
      },
      {
        heading: "Come si svolge l'assistenza",
        paragraphs: [
          "Il primo incontro è dedicato all'ascolto e all'esame della documentazione disponibile: lettera di assunzione, buste paga, comunicazioni con il datore di lavoro, eventuali verbali. Sulla base di questi elementi ti spiego con chiarezza quali sono le possibilità concrete, i tempi prevedibili e i costi.",
          "Quando è possibile, privilegio la via stragiudiziale — diffida, trattativa, tentativo di conciliazione — perché spesso consente di ottenere il risultato in tempi più brevi. Se la trattativa non porta a un esito adeguato, ti assisto nel giudizio davanti al Tribunale del lavoro.",
        ],
      },
      {
        heading: "Perché agire tempestivamente",
        paragraphs: [
          "I diritti del lavoratore sono soggetti a termini precisi: i crediti retributivi si prescrivono in genere in cinque anni, mentre per impugnare un licenziamento i termini sono molto più brevi. Rivolgersi subito a un avvocato consente di non perdere tutele importanti e di raccogliere per tempo le prove necessarie.",
        ],
      },
    ],
    related: ["licenziamento", "recupero-crediti-tfr"],
  },
  licenziamento: {
    slug: "licenziamento",
    navName: "Licenziamento",
    metaTitle: "Licenziamento: impugnazione e tutele | Avvocato a Palermo",
    metaDescription:
      "Hai ricevuto un licenziamento? Verifica dei termini di impugnazione, tutela reintegratoria e indennitaria. Assistenza legale a Palermo con l'Avv. Manfredi Bruno.",
    h1: "Licenziamento: impugnazione e tutele",
    kicker: "Diritto del lavoro",
    intro:
      "Ricevere una lettera di licenziamento è un momento delicato e i tempi per reagire sono brevi. Una valutazione tempestiva consente di verificare se il provvedimento sia legittimo, quali tutele possano essere applicate e quali iniziative sia necessario intraprendere entro i termini previsti dalla legge.",
    sections: [
      {
        heading: "I principali tipi di licenziamento",
        blocks: [
          { type: "p", text: "La legge prevede diverse ipotesi di licenziamento, ciascuna caratterizzata da presupposti e conseguenze differenti. Comprendere la ragione indicata dal datore di lavoro è il primo passo per valutare correttamente la situazione." },
          { type: "p", text: "Tra le principali ipotesi rientrano:" },
          {
            type: "list",
            items: [
              "**Licenziamento per giusta causa**: quando viene contestato al lavoratore un comportamento particolarmente grave, tale da non consentire la prosecuzione neppure provvisoria del rapporto di lavoro",
              "**Licenziamento per giustificato motivo soggettivo**: quando il lavoratore ha commesso un notevole inadempimento degli obblighi derivanti dal rapporto di lavoro",
              "**Licenziamento per giustificato motivo oggettivo**: quando il datore di lavoro indica ragioni legate all'attività produttiva, all'organizzazione del lavoro o al regolare funzionamento dell'impresa",
              "**Licenziamento collettivo**: nell'ambito di procedure di riduzione del personale disciplinate dalla legge",
              "**Licenziamento per superamento del periodo di comporto**: quando il lavoratore supera il periodo di conservazione del posto previsto in caso di malattia",
            ],
          },
          { type: "p", text: "I licenziamenti per giusta causa e per giustificato motivo soggettivo possono essere collegati a contestazioni disciplinari e richiedono la verifica del rispetto delle procedure previste e del diritto di difesa del lavoratore." },
        ],
      },
      {
        heading: "I termini per impugnare il licenziamento",
        blocks: [
          { type: "p", text: "I termini per contestare un licenziamento sono particolarmente importanti." },
          { type: "p", text: "In generale, il licenziamento deve essere impugnato entro **60 giorni** dalla ricezione della relativa comunicazione, attraverso un atto scritto idoneo a manifestare la volontà del lavoratore di contestare il provvedimento." },
          { type: "p", text: "Dopo l'impugnazione, entro i termini previsti dalla legge devono essere attivati gli ulteriori strumenti necessari per evitare l'inefficacia dell'impugnazione. A seconda della situazione, ciò può avvenire attraverso l'azione giudiziaria o mediante le procedure di conciliazione previste dall'ordinamento." },
          { type: "p", text: "La disciplina applicabile e il calcolo dei termini devono essere verificati attentamente nel singolo caso. Per questo è importante rivolgersi tempestivamente a un avvocato e far esaminare la lettera di licenziamento e la documentazione relativa al rapporto di lavoro." },
        ],
      },
      {
        heading: "Quali tutele sono possibili",
        blocks: [
          { type: "p", text: "Le conseguenze di un licenziamento illegittimo possono essere diverse." },
          { type: "p", text: "La tutela applicabile dipende, tra gli altri elementi, dal tipo di licenziamento, dal vizio riscontrato, dalla data di assunzione, dalle caratteristiche del rapporto di lavoro, dalle dimensioni del datore di lavoro e dalla disciplina applicabile al caso concreto." },
          { type: "p", text: "A seconda della situazione, possono essere previste:" },
          {
            type: "list",
            items: [
              "La reintegrazione nel posto di lavoro",
              "Un'indennità economica",
              "Altre forme di tutela previste dalla normativa applicabile",
            ],
          },
          { type: "p", text: "Esistono inoltre particolari ipotesi di licenziamento che possono essere considerate nulle, come, ad esempio, il licenziamento discriminatorio, il licenziamento orale o quello intimato in violazione di specifici divieti previsti dalla legge." },
          { type: "p", text: "Per comprendere quale tutela possa essere applicata è necessario analizzare attentamente il provvedimento di licenziamento, le ragioni indicate dal datore di lavoro e l'intera documentazione relativa al rapporto." },
        ],
      },
      {
        heading: "La possibilità di una conciliazione",
        blocks: [
          { type: "p", text: "Non tutte le controversie in materia di licenziamento arrivano fino alla conclusione di un giudizio." },
          { type: "p", text: "In alcuni casi può essere possibile raggiungere un accordo conciliativo tra lavoratore e datore di lavoro, in sede sindacale, presso gli organismi competenti o nell'ambito di una procedura giudiziaria." },
          { type: "p", text: "Una conciliazione può prevedere una soluzione economica concordata tra le parti e consentire di definire la controversia senza proseguire il giudizio." },
          { type: "p", text: "La possibilità e la convenienza di una soluzione conciliativa devono tuttavia essere valutate in base alle caratteristiche del singolo caso e agli interessi del lavoratore." },
        ],
      },
      {
        heading: "Cosa fare subito dopo il licenziamento",
        blocks: [
          { type: "p", text: "Se hai ricevuto una lettera di licenziamento, è importante non sottovalutare la situazione." },
          { type: "p", text: "In particolare:" },
          {
            type: "list",
            items: [
              "Conserva la lettera di licenziamento e tutte le comunicazioni ricevute",
              "Non firmare dimissioni, rinunce o accordi senza averli prima attentamente valutati",
              "Raccogli il contratto di assunzione, le buste paga, le eventuali contestazioni disciplinari e gli altri documenti utili",
              "Verifica tempestivamente la data in cui hai ricevuto la comunicazione, perché da questa possono decorrere termini importanti",
              "Rivolgiti rapidamente a un avvocato per valutare la legittimità del licenziamento e le possibili iniziative da intraprendere",
            ],
          },
        ],
      },
      {
        heading: "Perché è importante agire tempestivamente",
        blocks: [
          { type: "p", text: "Nel caso di licenziamento, attendere troppo può compromettere la possibilità di utilizzare gli strumenti di tutela previsti dalla legge." },
          { type: "p", text: "Una valutazione effettuata tempestivamente permette di verificare il rispetto delle procedure, individuare eventuali vizi del licenziamento, comprendere quali tutele possano essere applicate e decidere la strategia più adatta al caso concreto." },
          { type: "p", text: "Se hai ricevuto una lettera di licenziamento a Palermo o in provincia, puoi prenotare un appuntamento presso il mio studio per esaminare la documentazione e valutare insieme la tua situazione." },
        ],
      },
    ],
    related: ["diritto-del-lavoro", "recupero-crediti-tfr"],
  },
  "recupero-crediti-tfr": {
    slug: "recupero-crediti-tfr",
    navName: "Recupero crediti e TFR",
    metaTitle: "Recupero crediti da lavoro e TFR a Palermo | Avv. Manfredi Bruno",
    metaDescription:
      "Stipendi non pagati, TFR e differenze retributive: gli strumenti per recuperare le somme dovute, anche in caso di datore di lavoro insolvente. Studio a Palermo.",
    h1: "Recupero crediti da lavoro e TFR",
    kicker: "Diritto del lavoro",
    intro:
      "Quando il datore di lavoro non paga retribuzioni, mensilità o TFR, il lavoratore dispone di strumenti concreti per recuperare quanto gli spetta. L'intervento tempestivo è importante: i crediti di lavoro sono soggetti a prescrizione e, più passa il tempo, più il recupero può diventare difficile.",
    sections: [
      {
        heading: "Quali somme possono essere recuperate",
        paragraphs: [
          "Le somme non corrisposte dal datore di lavoro possono riguardare diverse voci, in costanza di rapporto o dopo la sua cessazione:",
        ],
        list: [
          "Retribuzioni mensili non pagate, in tutto o in parte",
          "Mensilità aggiuntive (tredicesima, quattordicesima) e ratei maturati",
          "Differenze retributive per inquadramento in livello inferiore",
          "Straordinari, lavoro festivo e notturno non retribuiti",
          "Trattamento di fine rapporto (TFR)",
          "Indennità sostitutiva del preavviso e altre indennità dovute alla cessazione",
        ],
      },
      {
        heading: "Attenzione alla prescrizione",
        paragraphs: [
          "I crediti retributivi e il TFR sono soggetti a termini di prescrizione, in genere di cinque anni, con decorrenze diverse a seconda della voce e del momento in cui il rapporto si è concluso. Una volta decorso il termine, il diritto non può più essere fatto valere. Per questo è opportuno non attendere: una semplice richiesta scritta, se correttamente formulata, può interrompere la prescrizione.",
        ],
      },
      {
        heading: "Gli strumenti per il recupero",
        paragraphs: [
          "Il primo strumento è la diffida formale al datore di lavoro, spesso seguita da un tentativo di conciliazione. Se il datore non adempie, è possibile chiedere al giudice un decreto ingiuntivo — uno strumento rapido ed efficace quando il credito è documentato — oppure promuovere una causa ordinaria. Ottenuto un titolo, si procede all'esecuzione forzata sui beni del datore.",
        ],
      },
      {
        heading: "Se il datore di lavoro è insolvente: il Fondo di garanzia INPS",
        paragraphs: [
          "Quando l'azienda è sottoposta a procedura concorsuale (fallimento, liquidazione giudiziale e simili) o risulta insolvente, il Fondo di garanzia INPS può corrispondere al lavoratore il TFR e le retribuzioni relative agli ultimi tre mesi del rapporto. Anche in questi casi la domanda richiede requisiti e documentazione specifici: ti assisto nella verifica dei presupposti e nella presentazione della richiesta.",
        ],
      },
    ],
    related: ["diritto-del-lavoro", "licenziamento"],
  },
  "diritto-previdenziale": {
    slug: "diritto-previdenziale",
    navName: "Diritto previdenziale",
    metaTitle: "Diritto previdenziale a Palermo: INPS e pensioni | Avv. Manfredi Bruno",
    metaDescription:
      "Assistenza legale nei rapporti con l'INPS a Palermo: pensioni, invalidità civile, ricorsi contro provvedimenti e prestazioni previdenziali. Avv. Manfredi Bruno.",
    h1: "Diritto previdenziale e rapporti con l'INPS",
    kicker: "Area di attività",
    intro:
      "I rapporti con l'INPS riguardano diritti essenziali: la pensione, le prestazioni di invalidità, i sostegni al reddito. Quando una domanda viene respinta, un provvedimento appare errato o l'istituto chiede indietro delle somme, è possibile difendersi. Assisto pensionati, lavoratori e cittadini di Palermo e provincia nel dialogo con l'INPS e nei ricorsi, amministrativi e giudiziari.",
    sections: [
      {
        heading: "In quali situazioni posso aiutarti",
        list: [
          "Domande di pensione o di prestazione respinte",
          "Errori nel calcolo dell'assegno pensionistico",
          "Mancato riconoscimento di periodi contributivi",
          "Provvedimenti di revoca di prestazioni già concesse",
          "Indebiti previdenziali e assistenziali: richieste di restituzione di somme da parte dell'INPS",
          "Ricorsi in materia di invalidità civile e prestazioni assistenziali",
        ],
      },
      {
        heading: "Le principali aree di attività previdenziale",
        paragraphs: [
          "Il diritto previdenziale comprende materie diverse, ciascuna con regole e termini propri. Nel mio studio seguo in particolare:",
        ],
        list: [
          "Pensioni di vecchiaia e anticipate, con verifica dei requisiti",
          "Pensione di inabilità e assegno ordinario di invalidità",
          "Invalidità civile, indennità di accompagnamento e prestazioni collegate",
          "Pensione di reversibilità e prestazioni ai superstiti",
          "Ricostituzioni, riscatti e totalizzazione dei contributi",
        ],
      },
      {
        heading: "Ricorso amministrativo e ricorso giudiziario",
        paragraphs: [
          "A seconda della materia, contro un provvedimento INPS può essere possibile un ricorso amministrativo ai comitati interni dell'istituto, oppure può essere necessario adire direttamente il giudice del lavoro. I termini variano e sono spesso brevi: per questo la prima cosa da fare è far esaminare il provvedimento ricevuto, per capire entro quando e con quale strumento è possibile reagire.",
        ],
      },
    ],
    related: ["inps-ricorsi", "pensioni", "invalidita-civile", "indebiti-inps-palermo"],
  },
  "inps-ricorsi": {
    slug: "inps-ricorsi",
    navName: "INPS e ricorsi",
    metaTitle: "Ricorsi contro l'INPS a Palermo | Avv. Manfredi Bruno",
    metaDescription:
      "L'INPS ha respinto la tua domanda o ti chiede indietro delle somme? Valutazione del provvedimento, ricorso amministrativo e giudiziario a Palermo.",
    h1: "Ricorsi contro l'INPS",
    kicker: "Diritto previdenziale",
    intro:
      "Un provvedimento sfavorevole dell'INPS non è necessariamente definitivo. Domande respinte, prestazioni revocate, calcoli errati e richieste di restituzione possono essere contestati con gli strumenti previsti dall'ordinamento. Il primo passo è analizzare il provvedimento e verificare entro quali termini è possibile reagire.",
    sections: [
      {
        heading: "Quando è possibile fare ricorso",
        list: [
          "La domanda di pensione o di altra prestazione è stata respinta",
          "L'INPS ha revocato una prestazione già riconosciuta",
          "L'assegno pensionistico è stato calcolato in modo errato",
          "Periodi contributivi non risultano accreditati",
          "L'INPS richiede la restituzione di somme percepite (indebito previdenziale)",
          "La domanda di rateizzazione o di rateo non è stata accolta",
        ],
      },
      {
        heading: "Ricorso amministrativo o ricorso giudiziario",
        paragraphs: [
          "L'ordinamento prevede strumenti diversi a seconda della materia. In alcuni casi è possibile presentare un ricorso amministrativo ai comitati dell'INPS, entro termini indicativamente di novanta giorni; in altri casi è necessario rivolgersi direttamente al giudice del lavoro. Scegliere lo strumento corretto e rispettare i termini è decisivo: un errore in questa fase può pregiudicare la possibilità di tutela.",
          "Durante il primo incontro esamino il provvedimento, verifico i termini applicabili al tuo caso e ti spiego quale percorso è percorribile, con quali probabilità e con quali costi.",
        ],
      },
      {
        heading: "Recuperi di somme e richieste di restituzione",
        paragraphs: [
          "Quando l'INPS richiede la restituzione di somme, non sempre la richiesta è corretta: possono esserci errori di calcolo, prescrizione di alcune annualità o cause che escludono la ripetizione. Anche quando la somma è dovuta, è spesso possibile ottenere una rateizzazione. Vale la pena far verificare il provvedimento prima di pagare o prima che il termine per opporsi decada.",
        ],
      },
      {
        heading: "Come si svolge il giudizio",
        paragraphs: [
          "Il ricorso giudiziario si svolge davanti al Tribunale, in composizione specializzata per le materie di lavoro e previdenza. Nelle questioni che riguardano lo stato di salute — ad esempio invalidità o inabilità — il giudice dispone normalmente una consulenza tecnica medica. Ti assisto in ogni fase, dalla redazione del ricorso fino all'esito del giudizio.",
        ],
      },
    ],
    related: ["diritto-previdenziale", "pensioni", "invalidita-civile", "indebiti-inps-palermo"],
  },
  "invalidita-civile": {
    slug: "invalidita-civile",
    navName: "Invalidità civile",
    metaTitle: "Invalidità civile: domande e ricorsi a Palermo | Avv. Manfredi Bruno",
    metaDescription:
      "Invalidità civile, assegno mensile, indennità di accompagnamento e Legge 104: assistenza nella domanda e nei ricorsi giudiziari a Palermo.",
    h1: "Invalidità civile: riconoscimento e ricorsi",
    kicker: "Diritto previdenziale",
    intro:
      "L'invalidità civile è il riconoscimento, da parte dello Stato, di una riduzione della capacità lavorativa o, per i minori e gli over 65, della capacità di svolgere i compiti della propria età. Quando la domanda viene respinta o la percentuale riconosciuta non corrisponde alla reale condizione di salute, è possibile presentare ricorso.",
    sections: [
      {
        heading: "Che cos'è l'invalidità civile",
        paragraphs: [
          "Il riconoscimento dell'invalidità civile, disciplinato dalla legge n. 118 del 1971, dà accesso a prestazioni diverse a seconda della percentuale riconosciuta e dei requisiti reddituali: dall'assegno mensile di assistenza (in presenza di una riduzione della capacità lavorativa pari o superiore al 74%) alle prestazioni per gli invalidi totali, fino all'indennità di accompagnamento per chi non è in grado di deambulare o di compiere gli atti quotidiani autonomamente.",
          "Dal riconoscimento possono derivare anche benefici di altra natura, come i permessi previsti dalla Legge 104 per i lavoratori e i loro familiari.",
        ],
      },
      {
        heading: "La domanda e la visita di accertamento",
        paragraphs: [
          "Il percorso inizia con il certificato medico telematico e prosegue con la domanda all'INPS e la visita davanti alla commissione medica. L'esito è contenuto nel verbale: può riconoscere l'invalidità con una certa percentuale, respingere la domanda o rinviare a una revisione. La documentazione sanitaria presentata ha un ruolo decisivo: conviene prepararla con cura.",
        ],
      },
      {
        heading: "Se la domanda viene respinta: il ricorso giudiziario",
        paragraphs: [
          "Contro il verbale è possibile proporre ricorso al giudice, in genere entro sei mesi dalla sua comunicazione. Nel giudizio viene normalmente disposta una consulenza tecnica d'ufficio: un medico nominato dal tribunale riesamina la situazione sanitaria. Molte domande respinte in sede amministrativa trovano esito favorevole in giudizio, quando la condizione di salute è adeguatamente documentata.",
        ],
      },
      {
        heading: "Aggravamento e revisione",
        paragraphs: [
          "Se le condizioni di salute peggiorano, è possibile chiedere l'aggravamento della percentuale riconosciuta; in senso opposto, l'INPS può disporre revisioni. Anche in questi casi posso assisterti nella valutazione della situazione e nella gestione del rapporto con l'istituto.",
        ],
      },
    ],
    landing: {
      path: "/ricorso-inps-invalidita-accompagnamento-palermo",
      label: "Ricorso INPS per invalidità civile e accompagnamento a Palermo",
    },
    related: ["diritto-previdenziale", "inps-ricorsi"],
  },
  pensioni: {
    slug: "pensioni",
    navName: "Pensioni",
    metaTitle: "Pensioni: verifica, domande e ricorsi a Palermo | Avv. Manfredi Bruno",
    metaDescription:
      "Pensione di vecchiaia, anticipata, di inabilità, assegno ordinario di invalidità e reversibilità: verifica della posizione e ricorsi INPS a Palermo.",
    h1: "Pensioni: verifica, domande e ricorsi",
    kicker: "Diritto previdenziale",
    intro:
      "La pensione è il risultato di una vita di lavoro: per questo errori di calcolo, contributi mancanti o domande respinte meritano di essere verificati con attenzione. Assisto lavoratori e pensionati di Palermo e provincia nella verifica della posizione contributiva, nella presentazione delle domande e nei ricorsi contro i provvedimenti INPS.",
    sections: [
      {
        heading: "Verificare la propria posizione contributiva",
        paragraphs: [
          "Prima della domanda di pensione è opportuno esaminare l'estratto conto contributivo: periodi mancanti, lavoro non accreditato o gestioni diverse possono incidere sul diritto e sulla misura dell'assegno. Quando emergono omissioni, è possibile attivarsi per la ricostituzione della posizione, anche attraverso i cosiddetti contributi da prescrizione e le prove documentali o testimoniali del lavoro svolto.",
        ],
      },
      {
        heading: "Le principali prestazioni pensionistiche",
        list: [
          "Pensione di vecchiaia, al raggiungimento dei requisiti anagrafici e contributivi",
          "Pensione anticipata, per chi ha maturato il requisito contributivo richiesto",
          "Pensione di inabilità, per i lavoratori con assoluta e permanente impossibilità a svolgere attività lavorativa",
          "Assegno ordinario di invalidità, per chi ha una riduzione significativa della capacità lavorativa",
          "Pensione di reversibilità e prestazioni ai familiari superstiti",
        ],
      },
      {
        heading: "Errori nel calcolo della pensione",
        paragraphs: [
          "L'assegno pensionistico può essere calcolato in modo errato per contributi non considerati, retribuzioni non correttamente valorizzate o errori materiali. In questi casi è possibile chiedere la ricostituzione della pensione e, se l'INPS non provvede, ricorrere al giudice per ottenere la differenza dovuta, nei limiti dei termini di prescrizione.",
        ],
      },
      {
        heading: "Se l'INPS respinge la domanda",
        paragraphs: [
          "Un rigetto non è necessariamente definitivo: può derivare da un errore nella valutazione dei requisiti o da documentazione incompleta. È importante far esaminare tempestivamente il provvedimento, per individuare lo strumento di tutela corretto — ricorso amministrativo o giudiziario — entro i termini previsti.",
        ],
      },
    ],
    related: ["diritto-previdenziale", "inps-ricorsi"],
  },
  "indebiti-inps-palermo": {
    slug: "indebiti-inps-palermo",
    navName: "Indebiti INPS e restituzioni",
    metaTitle: "Indebiti INPS e richieste di restituzione | Avvocato Previdenziale Palermo",
    metaDescription:
      "L'INPS ti ha chiesto di restituire delle somme? Verifico se la richiesta di restituzione è legittima e valuto le possibili forme di tutela. Assistenza a Palermo per indebiti previdenziali e assistenziali.",
    h1: "Indebiti previdenziali e assistenziali: le richieste di restituzione dell'INPS",
    kicker: "Diritto previdenziale",
    intro:
      "Può accadere che l'INPS richieda la restituzione di somme ritenute indebitamente percepite, ad esempio una pensione, una prestazione di invalidità civile o un'altra prestazione previdenziale o assistenziale. Ricevere una richiesta di restituzione dall'INPS non significa automaticamente dover pagare senza verifiche. Il primo passo è comprendere l'origine della richiesta, esaminare il provvedimento ricevuto e valutare se le somme richieste siano effettivamente dovute.",
    sections: [
      {
        heading: "Quando l'INPS chiede indietro delle somme",
        paragraphs: [
          "L'INPS può richiedere la restituzione di somme che ritiene siano state erogate senza che ne ricorressero i presupposti.",
          "Le richieste possono riguardare pensioni, prestazioni previdenziali, invalidità civile e altre prestazioni assistenziali o a sostegno del reddito.",
          "Le cause possono essere diverse: ricalcoli dell'importo della prestazione, superamento dei limiti reddituali previsti, successive verifiche o accertamenti, modifiche della situazione del beneficiario oppure errori nella gestione della pratica.",
          "L'importo richiesto può essere anche rilevante e, a seconda della prestazione interessata e delle modalità di recupero previste, l'INPS può procedere al recupero delle somme anche attraverso trattenute sulle prestazioni erogate.",
        ],
      },
      {
        heading: "Una richiesta di restituzione va sempre verificata",
        paragraphs: [
          "Non ogni richiesta di restituzione è corretta e non sempre l'intero importo richiesto è effettivamente dovuto.",
          "Prima di assumere qualsiasi decisione è opportuno esaminare con attenzione:",
        ],
        list: [
          "L'origine del presunto indebito e le ragioni indicate dall'INPS",
          "Il periodo interessato dalla richiesta di restituzione",
          "I provvedimenti e le comunicazioni ricevute, con le relative date",
          "La correttezza dei calcoli effettuati e dei dati utilizzati",
          "La situazione reddituale o personale rilevante ai fini della prestazione",
          "L'eventuale prescrizione di una o più annualità richieste",
        ],
      },
      {
        heading: "Quando l'indebito può essere contestato",
        paragraphs: [
          "In alcuni casi la richiesta dell'INPS può essere contestabile.",
          "Può accadere, ad esempio, che la richiesta si fondi su errori nei calcoli o nei dati utilizzati, che alcune somme richieste riguardino periodi per i quali debba essere verificata l'eventuale prescrizione oppure che la situazione reddituale, personale o sanitaria del beneficiario sia stata valutata in modo non corretto.",
          "Anche le ragioni indicate nel provvedimento e la documentazione utilizzata dall'INPS devono essere attentamente esaminate.",
          "A seconda del caso concreto, può essere possibile valutare l'utilizzo degli strumenti di tutela previsti in sede amministrativa o giudiziaria.",
          "In alcuni casi l'esame della situazione può portare all'annullamento o alla riduzione dell'importo richiesto; in altri, invece, può confermare che la restituzione sia dovuta.",
          "Ogni situazione richiede una valutazione specifica: per questo l'analisi deve partire sempre dal provvedimento ricevuto e dalla documentazione relativa alla prestazione.",
        ],
      },
      {
        heading: "Cosa fare quando ricevi una richiesta di restituzione dall'INPS",
        paragraphs: [
          "Se ricevi una comunicazione con cui l'INPS richiede la restituzione di somme, è importante non sottovalutarla.",
          "In particolare:",
        ],
        list: [
          "Non ignorare la comunicazione ricevuta",
          "Conserva il provvedimento completo e la prova della data in cui lo hai ricevuto",
          "Raccogli la documentazione relativa alla pensione o alla prestazione interessata",
          "Verifica attentamente le somme e il periodo cui si riferisce la richiesta",
          "Prima di effettuare un pagamento o assumere altre decisioni, fai esaminare la documentazione",
          "Rivolgiti tempestivamente a un professionista per valutare la tua situazione",
        ],
      },
      {
        heading: "Se la somma è dovuta: rateizzazione e possibili soluzioni",
        paragraphs: [
          "Quando dall'esame della situazione emerge che la restituzione è dovuta, il pagamento immediato dell'intero importo potrebbe non essere l'unica soluzione.",
          "A seconda del tipo di indebita percezione, della prestazione interessata e delle modalità previste, può essere possibile valutare forme di pagamento rateale o altre soluzioni utili a rendere più sostenibile il recupero delle somme.",
          "Anche in questa fase è importante comprendere correttamente le modalità di recupero applicabili e valutare la soluzione più adeguata alla situazione concreta.",
        ],
      },
      {
        heading: "Perché è importante agire tempestivamente",
        paragraphs: [
          "Le modalità e i termini per contestare una richiesta dell'INPS possono variare in base al tipo di prestazione e al provvedimento ricevuto.",
          "Per questo motivo è importante non attendere e far esaminare tempestivamente la documentazione.",
          "Una valutazione effettuata in tempi utili permette di comprendere se la richiesta sia corretta, quali strumenti di tutela possano eventualmente essere utilizzati e quale strategia sia più adatta al caso concreto.",
          "Se hai ricevuto una richiesta di restituzione dall'INPS a Palermo o in provincia, puoi prenotare un appuntamento presso il mio studio per esaminare insieme la documentazione e valutare la tua situazione.",
        ],
      },
    ],
    related: ["inps-ricorsi", "pensioni", "invalidita-civile"],
    articles: [
      "inps-restituzione-somme-cosa-fare",
      "indebito-pensionistico-inps-quando-contestato",
      "indebito-assistenziale-richiesta-restituzione-inps",
      "richiesta-restituzione-inps-quando-avvocato",
    ],
  },
};

export const getArea = (slug) => PRACTICE_AREAS[slug];
